<template>
  <BaseCard
    :heading="filters.capitalize($t('global.summary'))"
    :loading="!order"
  >
    <Flex
      direction="column"
      :class="$style.orderItems"
      justify="between"
    >
      <AppointmentDiscountCode
        v-for="discountCode in discountCodes"
        :key="discountCode.code"
        :discountCode="discountCode"
        size="s"
        v-test="'sale-summary-discount-code'"
      />
      <template
        v-for="row in rows"
        :key="row.key"
      >
        <ServicePackageItem
          v-if="row.kind === 'package'"
          :name="row.name"
          :serviceNames="row.serviceNames"
          :quantity="row.quantity"
          :price="row.price"
          :originalPrice="row.originalPrice"
          :discountPercentage="row.discountPercentage"
        />
        <OrderItem
          v-else
          :item="row.item"
        />
      </template>
    </Flex>
    <Flex
      direction="column"
      :class="[$style.transactions, { [$style.lastSection]: !showTipAlert }]"
    >
      <Flex
        align="center"
        justify="between"
      >
        <BaseHeading size="s">
          {{ order.transactions?.length ? $t('global.total') : $t('global.total_outstanding') }}
        </BaseHeading>
        <BaseText
          v-test="'sale-summary-total-price'"
          bold
        >
          {{ filters.currency(order.total) }}
        </BaseText>
      </Flex>
      <Transactions
        v-if="!!order.transactions?.length"
        :transactions="order.transactions"
      />
      <Flex
        v-if="order.amountOutstanding && order.amountOutstanding < 0"
        align="center"
        justify="between"
      >
        <BaseHeading size="s">{{ $t('register.change_money') }}</BaseHeading>
        <BaseText v-test="'sale-summary-change-money'">
          {{ filters.currency(Math.abs(order.amountOutstanding)) }}
        </BaseText>
      </Flex>
      <Flex
        v-if="order.amountOutstanding && order.amountOutstanding > 0 && !!order.transactions?.length"
        align="center"
        justify="between"
      >
        <BaseHeading size="s">{{ $t('global.outstanding') }}</BaseHeading>
        <BaseText
          v-test="'sale-summary-outstanding-amount'"
          bold
        >
          {{ filters.currency(order.amountOutstanding) }}
        </BaseText>
      </Flex>
    </Flex>
    <Flex
      v-if="showTipAlert"
      direction="column"
      :class="$style.tipAlert"
    >
      <BaseAlert
        color="success"
        size="s"
        :text="`🎉 ${$t('register.pos_successful_transaction.tip_message', { client: order.customer?.firstName || $t('global.items.customer'), amount: filters.currency(order.tipAmount) })}`"
        v-test="'sale-summary-tip'"
      />
    </Flex>
  </BaseCard>
</template>

<script
  setup
  lang="ts"
>
import { computed } from 'vue';
import AppointmentDiscountCode from '@/components/AppointmentDiscountCode.vue';
import filters from '@/filters';
import { uniqueDiscountCodes } from '@/helpers/formatting';
import { useOrderSummaryRows } from '@/helpers/order-summary-rows';
import type { Order } from '@/types';
import OrderItem from './OrderItem.vue';
import ServicePackageItem from './ServicePackageItem.vue';
import Transactions from './Transactions.vue';

defineOptions({
  name: 'SaleSummary',
});

const props = defineProps<{
  order: Order;
}>();

const rows = useOrderSummaryRows(() =>
  (props.order?.items ?? []).map((item) => ({
    ...item,
    name: item.description,
    servicePackageServiceId: item.servicePackageService?.id,
  })),
);

const showTipAlert = computed(() => props.order?.tipAmount);

const discountCodes = computed(() => {
  return props.order?.appointments?.length
    ? uniqueDiscountCodes(props.order?.appointments)
    : [];
});
</script>

<style
  lang="scss"
  module
>
.orderItems {
  padding-bottom: $spacing;
  border-bottom: 1px solid var(--color-border);
}

.transactions {
  &.lastSection {
    padding-top: $spacing;
  }

  &:not(.lastSection) {
    padding: $spacing 0;
  }
}

.tipAlert {
  padding-top: $spacing * 1.5;
  padding-bottom: $spacing * 0.5;
  border-top: 1px solid var(--color-border);
}
</style>
